import type { ReferenceLineMetadata } from '../../../types/ChartContext';
import { getScaleInput } from '../../scaleHelpers';
import type { DataPointSnapResult, ReferenceLineSnapResult, SnapStrategy, SnapStrategyContext } from '../types';

/**
 * Abstract base class for snap strategies.
 * Provides shared utilities and default implementations.
 *
 * When creating a new chart type strategy:
 * 1. Extend this class
 * 2. Override abstract methods: findClosestDataPoint, shouldShowCrosshair
 * 3. Optionally override: findClosestReferenceLine, gatherNearbyReferenceLines
 */
export abstract class BaseSnapStrategy implements SnapStrategy {
  /**
   * Bandwidth for band scales (column charts). Zero for continuous scales.
   */
  protected bandwidth: number;

  constructor(bandwidth: number = 0) {
    this.bandwidth = bandwidth;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Protected Helpers - Available to all subclasses
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Checks if a reference line has tooltip content (tooltipName or events).
   */
  protected hasTooltipContent(metadata: ReferenceLineMetadata): boolean {
    return Boolean(metadata.tooltipName || (metadata.events && metadata.events.length > 0));
  }

  /**
   * Gets the appropriate scale input value, handling zoom state and band scales.
   * For string values (categories), returns them directly.
   * For numeric values, converts to string for band scales.
   */
  protected getScaleInputForValue(value: number | string, context: SnapStrategyContext): string | number {
    // String values (categories) are used directly
    if (typeof value === 'string') {
      return value;
    }

    const { xScaleType, zoomDomain } = context;

    if (xScaleType && zoomDomain !== undefined) {
      return getScaleInput(value, this.bandwidth > 0);
    }

    return value;
  }

  /**
   * Calculates the X position, optionally centering for band scales.
   */
  protected getXPosition(scaledX: number, centerForBandScale: boolean = false): number {
    if (centerForBandScale && this.bandwidth > 0) {
      return scaledX + this.bandwidth / 2;
    }
    return scaledX;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Abstract Methods - Must be implemented by subclasses
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Finds the closest data point based on cursor position.
   * Chart-type-specific behavior (e.g., point-based vs band-based snapping).
   */
  abstract findClosestDataPoint(context: SnapStrategyContext): DataPointSnapResult | null;

  /**
   * Determines if the crosshair should be shown.
   * Chart-type-specific (e.g., always show vs only on reference lines).
   */
  abstract shouldShowCrosshair(hasDataPoint: boolean, hasNearbyReferenceLine: boolean): boolean;

  // ─────────────────────────────────────────────────────────────────────────────
  // Default Implementations - Can be overridden by subclasses
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Finds the closest reference line based on cursor position.
   * Default implementation using point-based snapping with optional band centering.
   */
  findClosestReferenceLine(
    context: SnapStrategyContext,
    referenceLines: Map<string, ReferenceLineMetadata>,
  ): ReferenceLineSnapResult | null {
    const { cursorSvgX, xScale, snapDistance } = context;

    let result: ReferenceLineSnapResult | null = null;

    for (const metadata of referenceLines.values()) {
      if (metadata.type !== 'vertical' || !this.hasTooltipContent(metadata)) {
        continue;
      }

      const scaleInput = this.getScaleInputForValue(metadata.point, context);
      const scaledX = xScale(scaleInput as number);
      if (typeof scaledX !== 'number') {
        continue;
      }

      const xPosition = this.getXPosition(scaledX, true);
      const distance = Math.abs(xPosition - cursorSvgX);
      if (distance <= snapDistance && (!result || distance < result.distance)) {
        result = { point: metadata.point, xPosition, distance };
      }
    }

    return result;
  }

  /**
   * Gathers reference lines that are near a given position.
   * Default implementation checks if reference lines are within 5px.
   */
  gatherNearbyReferenceLines(
    xPosition: number,
    referenceLines: Map<string, ReferenceLineMetadata>,
    context: SnapStrategyContext,
  ): ReferenceLineMetadata[] {
    const { xScale } = context;
    const result: ReferenceLineMetadata[] = [];

    for (const metadata of referenceLines.values()) {
      if (metadata.type !== 'vertical' || !this.hasTooltipContent(metadata)) {
        continue;
      }

      const scaleInput = this.getScaleInputForValue(metadata.point, context);
      const scaledX = xScale(scaleInput as number);
      if (typeof scaledX !== 'number') {
        continue;
      }

      const refLinePosition = this.getXPosition(scaledX, true);
      if (Math.abs(refLinePosition - xPosition) <= 5) {
        result.push(metadata);
      }
    }

    return result;
  }
}
